import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = API;

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
});

// Automatically attach current warehouse_id from localStorage to every request
api.interceptors.request.use((config) => {
  const wh = localStorage.getItem('stockaris_warehouse_id');
  if (wh) {
    config.headers['X-Warehouse-Id'] = wh;
  }
  return config;
});

// SKUs
export const getSkus = (params) => api.get('/skus', { params });
export const getSku = (id) => api.get(`/skus/${id}`);
export const createSku = (data) => api.post('/skus', data);
export const updateSku = (id, data) => api.put(`/skus/${id}`, data);
export const deleteSku = (id) => api.delete(`/skus/${id}`);
// iter164 — Multi-UOM management
export const getSkuUoms = (sku_id) => api.get(`/skus/${sku_id}/uoms`);
export const addSkuUom = (sku_id, body) => api.post(`/skus/${sku_id}/uoms`, body);
export const removeSkuUom = (sku_id, code) => api.delete(`/skus/${sku_id}/uoms/${encodeURIComponent(code)}`);

// iter166 — On-Hand Inventory Report (CSV download bypasses axios JSON parsing)
export const getOnHandReport = (params) => api.get('/inventory/on-hand-report', { params });
export const downloadOnHandReportCsv = (params = {}) => {
  const qs = new URLSearchParams({ ...params, format: 'csv' }).toString();
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/on-hand-report?${qs}`;
  // Re-uses the same cookie auth as axios; opening a new tab triggers a browser download
  // via the Content-Disposition header set by FastAPI.
  window.open(url, '_blank');
};

// iter167 — self-service profile
export const changeMyPassword = (body) => api.post('/auth/change-password', body);
export const changeMyEmail = (body) => api.post('/auth/change-email', body);

// iter169 — bulk CSV stock load (admin warehouse setup)
export const bulkLoadStock = (file, mode, dryRun) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('mode', mode);
  fd.append('dry_run', dryRun ? 'true' : 'false');
  return api.post('/inventory/bulk-load', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const downloadBulkLoadTemplate = () => {
  window.open(`${process.env.REACT_APP_BACKEND_URL}/api/inventory/bulk-load/template`, '_blank');
};

// iter171 — per-tenant integration configs (self-serve API setup)
export const getIntegrationCatalog = () => api.get('/integrations/catalog');
export const listIntegrationConfigs = () => api.get('/integrations');
export const upsertIntegrationConfig = (provider, body) => api.put(`/integrations/${provider}`, body);
export const toggleIntegrationConfig = (provider) => api.post(`/integrations/${provider}/toggle`);
export const testIntegrationConfig = (provider) => api.post(`/integrations/${provider}/test`);
export const deleteIntegrationConfig = (provider) => api.delete(`/integrations/${provider}`);
export const listIntegrationEvents = (limit = 50) => api.get('/integrations/events', { params: { limit } });
export const listProviderEvents = (provider, limit = 25) => api.get(`/integrations/${provider}/events`, { params: { limit } });

// iter173 — stock valuation report + dashboard widget
export const getStockValuation = (params) => api.get('/reports/stock-valuation', { params });
export const downloadStockValuationCsv = (params) => {
  const q = new URLSearchParams({ ...(params || {}), format: 'csv' }).toString();
  window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/stock-valuation?${q}`, '_blank');
};

// iter175 — Warehouse Layout Designer
export const getTileKinds = () => api.get('/warehouse-layout/kinds');
export const getWarehouseLayout = (whId) => api.get(`/warehouses/${whId}/layout`);
// Inbound capacity + reservations
export const getInboundCapacity = (whId, days = 30) =>
  api.get(`/warehouses/${whId}/inbound-capacity`, { params: { horizon_days: days } });
export const getAllInboundCapacitySummary = (days = 30) =>
  api.get(`/warehouses/inbound-capacity-summary`, { params: { horizon_days: days } });
export const listContainerReservations = (cid) => api.get(`/containers/${cid}/reservations`);
export const addContainerReservation = (cid, payload) =>
  api.post(`/containers/${cid}/reservations`, payload);
export const removeContainerReservation = (cid, resId) =>
  api.delete(`/containers/${cid}/reservations/${resId}`);
export const autoReserveContainer = (cid) => api.post(`/containers/${cid}/reservations/auto`);
// SKU volume auditor
export const getSkuVolumeAudit = () => api.get('/sku-audit/');
export const downloadSkuVolumeAuditTemplate = () =>
  api.get('/sku-audit/template', { responseType: 'blob' });
export const uploadSkuVolumeAudit = (file, dryRun = false) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/sku-audit/upload?dry_run=${dryRun}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Transfer invoicing (dispatch note, GRN, settlement invoice, rate cards, ledger)
export const getTransferRateCard = (whId) => api.get(`/warehouses/${whId}/transfer-rates`);
export const saveTransferRateCard = (whId, body) => api.put(`/warehouses/${whId}/transfer-rates`, body);
export const listTransferDocuments = (transferId) => api.get(`/transfers/${transferId}/documents`);
export const generateDispatchNote = (transferId) => api.post(`/transfers/${transferId}/dispatch-note`);
export const generateGrn = (transferId, receivedLines) =>
  api.post(`/transfers/${transferId}/grn`, receivedLines || null);
export const settleTransfer = (transferId, body) => api.post(`/transfers/${transferId}/settle`, body);
export const markSettlementPaid = (settlementId) =>
  api.post(`/transfers/settlement/${settlementId}/mark-paid`);
export const downloadTransferDocPdf = (docId) =>
  api.get(`/transfers/documents/${docId}.pdf`, { responseType: 'blob' });
export const getTransferLedger = (params = {}) => api.get('/transfer-ledger', { params });

// Freight lane matrix
export const listFreightLanes = () => api.get('/freight-lanes');
export const saveFreightLane = (fromId, toId, body) => api.put(`/freight-lanes/${fromId}/${toId}`, body);
export const deleteFreightLane = (fromId, toId) => api.delete(`/freight-lanes/${fromId}/${toId}`);
export const getFreightLaneHistory = (fromId, toId) =>
  api.get(`/freight-lanes/${fromId}/${toId}/history`);
export const batchSettleTransfers = (body) => api.post('/transfers/batch-settle', body);

// Transfer flow bridges (pick + inbound receipt)
export const startTransferPick = (transferId) => api.post(`/transfers/${transferId}/start-pick`);
export const createTransferInboundReceipt = (transferId, autoAllocate = true) =>
  api.post(`/transfers/${transferId}/create-inbound-receipt`, { auto_allocate: autoAllocate });

// Zone palette — colour + label overrides for warehouse zones
export const listZonePalette = () => api.get('/zone-palette');
export const getZonePaletteDefaults = () => api.get('/zone-palette/defaults');
export const seedZonePaletteDefaults = () => api.post('/zone-palette/seed-defaults');
export const printTruckManifest = (body) =>
  api.post('/transfer-truck-manifest.pdf', body, { responseType: 'blob' });
export const saveZonePaletteEntry = (zoneCode, body) => api.put(`/zone-palette/${zoneCode}`, body);
export const deleteZonePaletteEntry = (zoneCode) => api.delete(`/zone-palette/${zoneCode}`);
export const exportLayoutPdf = (whId) =>
  api.get(`/warehouses/${whId}/layout/export/pdf`, { responseType: 'blob' });
export const saveWarehouseLayout = (whId, body) => api.put(`/warehouses/${whId}/layout`, body);
export const listLayoutLocations = (whId) => api.get(`/warehouses/${whId}/layout/locations`);
export const getLayoutLinkSuggestions = (whId, tiles) => api.post(`/warehouses/${whId}/layout/link-suggestions`, { tiles });
export const generateRackLocations = (whId, body) => api.post(`/warehouses/${whId}/layout/rack-locations`, body);
export const getLocationContents = (whId, locationIds = null) => api.post(`/warehouses/${whId}/layout/location-contents`, { location_ids: locationIds });
export const getLevelHeatmap = (whId, days = 90) => api.get(`/warehouses/${whId}/layout/level-heatmap`, { params: { days } });
export const scanPickBin = (pickListId, itemIndex, code) => api.post(`/pick-lists/${pickListId}/scan-bin`, { item_index: itemIndex, location_barcode: code });


// Serial-number tracking (iter200) — per-unit unique IDs for high-value SKUs
export const receiveSerials = (body) => api.post('/serials/receive', body);
export const listSerials = (params) => api.get('/serials', { params });
export const getSerial = (id) => api.get(`/serials/${id}`);
export const listSerialsForSku = (skuId, params) => api.get(`/skus/${skuId}/serials`, { params });
export const allocateSerial = (id, body) => api.post(`/serials/${id}/allocate`, body || {});
export const shipSerial = (id, body) => api.post(`/serials/${id}/ship`, body || {});
export const returnSerial = (id, body) => api.post(`/serials/${id}/return`, body);
export const quarantineSerial = (id, body) => api.post(`/serials/${id}/quarantine`, body || {});
export const releaseSerial = (id) => api.post(`/serials/${id}/release`);
export const pickSerial = (body) => api.post('/serials/pick', body);
export const importSerialsCsv = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/serials/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
// iter203: lookup by serial_no returns {serial, order, dispatch}
export const lookupSerial = (serial_no) => api.get(`/serials/lookup/${encodeURIComponent(serial_no)}`);
// iter203: pallet grouping
export const createSerialPallet = (body) => api.post('/serial-pallets', body);
export const listSerialPallets = (params) => api.get('/serial-pallets', { params });
export const getSerialPallet = (id) => api.get(`/serial-pallets/${id}`);
export const transferSerialPallet = (id, body) => api.post(`/serial-pallets/${id}/transfer`, body);
export const dissolveSerialPallet = (id) => api.post(`/serial-pallets/${id}/dissolve`);
export const palletQrStickersPdfUrl = (palletId) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/serial-pallets/${palletId}/qr-stickers.pdf`;
// iter203: packing slip PDF
export const packingSlipPdfUrl = (packTaskId) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/pack-tasks/${packTaskId}/packing-slip.pdf`;

// iter176 — Phase 2: ABC Heat Map, Phase 3: Simulator + Playback, Phase 4: Scenarios
export const getAbcOverlay = (whId, days = 90) => api.get(`/warehouses/${whId}/layout/abc-overlay`, { params: { days } });
export const getLayoutSimulation = (whId, days = 30) => api.get(`/warehouses/${whId}/layout/simulation`, { params: { days } });
export const getWarehouseLayoutKpis = (whId, days = 30, labourRate) => api.get(`/warehouses/${whId}/kpis`, { params: { days, ...(labourRate ? { labour_rate: labourRate } : {}) } });

// iter207 — Stock Discrepancy Investigation
export const investigateDiscrepancy = (payload) => api.post('/discrepancies/investigate', payload);
export const findStockElsewhere = (variantId, excludeLocationId) => api.get('/discrepancies/find-elsewhere', { params: { variant_id: variantId, ...(excludeLocationId ? { exclude_location_id: excludeLocationId } : {}) } });
export const getDiscrepancyStats = (days = 30) => api.get('/discrepancies/stats', { params: { days } });
export const getStockConfidence = (variantId, locationId) => api.get('/discrepancies/confidence', { params: { variant_id: variantId, ...(locationId ? { location_id: locationId } : {}) } });

// iter208 — Smart prioritisation, alerts, location correction
export const getSuggestedCounts = (limit = 25) => api.get('/discrepancies/suggested-counts', { params: { limit } });
export const runDiscrepancyWatcher = () => api.post('/discrepancies/watcher/run');
export const listDiscrepancyAlerts = (status = 'active') => api.get('/discrepancies/alerts', { params: { status } });
export const dismissDiscrepancyAlert = (alertId, note) => api.post(`/discrepancies/alerts/${alertId}/dismiss`, { note });
export const correctLocation = (payload) => api.post('/discrepancies/correct-location', payload);

// iter209 — State machine, alert delivery is server-side, KPI, auto-seed counts
export const openCase = (payload) => api.post('/discrepancies/cases', payload);
export const listCases = (state) => api.get('/discrepancies/cases', { params: state ? { state } : {} });
export const transitionCase = (caseId, payload) => api.post(`/discrepancies/cases/${caseId}/transition`, payload);
export const getLocationAccuracy = (days = 30) => api.get('/discrepancies/kpis/location-accuracy', { params: { days } });
export const seedCycleCountsFromSuggestions = (payload) => api.post('/discrepancies/seed-cycle-counts', payload);

// iter210 — Kanban board + SLA-per-reason-code
export const getDiscrepancyBoard = (limitPerColumn = 50) => api.get('/discrepancies/board', { params: { limit_per_column: limitPerColumn } });
export const getDiscrepancySla = () => api.get('/discrepancies/sla');
export const updateDiscrepancySla = (patch) => api.put('/discrepancies/sla', patch);
export const runDiscrepancySlaWatcher = () => api.post('/discrepancies/sla/watcher/run');

// iter211 — Case templates + SLA recipients
export const listCaseTemplates = () => api.get('/discrepancies/case-templates');
export const createCaseTemplate = (payload) => api.post('/discrepancies/case-templates', payload);
export const deleteCaseTemplate = (id) => api.delete(`/discrepancies/case-templates/${id}`);
export const applyCaseTemplate = (caseId, payload) => api.post(`/discrepancies/cases/${caseId}/apply-template`, payload);

// iter213 — Stockaris Transport (TMS)
export const listTransportVehicles = (status) => api.get('/transport/vehicles', { params: status ? { status } : {} });
// iter232 — Phase 12 Recurring Runs
export const listRecurringTemplates = (active) =>
  api.get('/transport/recurring-templates', { params: active == null ? {} : { active } });
export const createRecurringTemplate = (payload) =>
  api.post('/transport/recurring-templates', payload);
export const updateRecurringTemplate = (id, payload) =>
  api.put(`/transport/recurring-templates/${id}`, payload);
export const deleteRecurringTemplate = (id) =>
  api.delete(`/transport/recurring-templates/${id}`);
export const pauseRecurringTemplate = (id) =>
  api.post(`/transport/recurring-templates/${id}/pause`);
export const previewRecurringTemplate = (id, days = 14, dateFrom = null) =>
  api.post(`/transport/recurring-templates/${id}/preview`, null, {
    params: { days, ...(dateFrom ? { date_from: dateFrom } : {}) },
  });
export const spawnRecurringTemplate = (id, targetDate = null, force = false) =>
  api.post(`/transport/recurring-templates/${id}/spawn`, null, {
    params: {
      ...(targetDate ? { target_date: targetDate } : {}),
      ...(force ? { force: true } : {}),
    },
  });
export const createTransportVehicle = (p) => api.post('/transport/vehicles', p);
export const updateTransportVehicle = (id, p) => api.put(`/transport/vehicles/${id}`, p);
export const deleteTransportVehicle = (id) => api.delete(`/transport/vehicles/${id}`);
export const listTransportDrivers = () => api.get('/transport/drivers');
export const createTransportDriver = (p) => api.post('/transport/drivers', p);
export const updateTransportDriver = (id, p) => api.put(`/transport/drivers/${id}`, p);
export const deleteTransportDriver = (id) => api.delete(`/transport/drivers/${id}`);
export const listTransportJobs = (params = {}) => api.get('/transport/jobs', { params });
export const createTransportJob = (p) => api.post('/transport/jobs', p);
export const updateTransportJob = (id, p) => api.put(`/transport/jobs/${id}`, p);
export const deleteTransportJob = (id) => api.delete(`/transport/jobs/${id}`);
export const transitionTransportJob = (id, p) => api.post(`/transport/jobs/${id}/transition`, p);
export const autoCreateTransportJob = (p) => api.post('/transport/jobs/auto-create', p);
export const assignTransportJob = (id, p) => api.post(`/transport/jobs/${id}/assign`, p);
export const unassignTransportJob = (id) => api.post(`/transport/jobs/${id}/unassign`);
export const getTransportBoard = (date) => api.get('/transport/board', { params: date ? { date } : {} });

// iter214 — Route sequencing (OpenRouteService)
export const getOrsStatus = () => api.get('/transport/ors/status');
export const listDepots = () => api.get('/transport/depots');
export const planRoute = (p) => api.post('/transport/routes/plan', p);
export const autoSequenceRoute = (p) => api.post('/transport/routes/auto-sequence', p);
export const setRouteSequence = (order) => api.post('/transport/routes/set-sequence', { order });

// iter215 — Driver PWA + POD
export const issueDriverToken = (driverId) => api.get(`/transport/driver/token/${driverId}`);
export const getDriverRun = (params = {}) => api.get('/transport/driver/run', { params });
export const driverArrive = (jobId, p) => api.post(`/transport/driver/jobs/${jobId}/arrive`, p);
export const driverDepart = (jobId, p) => api.post(`/transport/driver/jobs/${jobId}/depart`, p);
export const driverPod = (jobId, p) => api.post(`/transport/driver/jobs/${jobId}/pod`, p);
// iter216 — Live GPS + customer tracking
export const driverPing = (jobId, p) => api.post(`/transport/driver/jobs/${jobId}/ping`, p);
// iter232 — multi-stop milk run per-stop endpoints
export const driverArriveStop = (jobId, seq, p) => api.post(`/transport/driver/jobs/${jobId}/stops/${seq}/arrive`, p);
export const driverDepartStop = (jobId, seq, p) => api.post(`/transport/driver/jobs/${jobId}/stops/${seq}/depart`, p);
export const driverPodStop = (jobId, seq, p) => api.post(`/transport/driver/jobs/${jobId}/stops/${seq}/pod`, p);
export const getJobTrackingLink = (jobId) => api.get(`/transport/jobs/${jobId}/tracking-link`);
export const getStopTrackingLink = (jobId, seq) => api.get(`/transport/jobs/${jobId}/stops/${seq}/tracking-link`);
export const getPublicTracking = (token) => api.get(`/transport/track/${token}`);

// iter218 — Freight rating + live ETA
export const listFreightRates = () => api.get('/transport/freight-rates');
export const createFreightRate = (p) => api.post('/transport/freight-rates', p);
export const updateFreightRate = (id, p) => api.put(`/transport/freight-rates/${id}`, p);
export const deleteFreightRate = (id) => api.delete(`/transport/freight-rates/${id}`);
export const rateJob = (jobId, p = {}) => api.post(`/transport/jobs/${jobId}/rate`, p);
export const recalcEta = (jobId) => api.post(`/transport/jobs/${jobId}/recalc-eta`);

// iter219 — Bulk rate import + Driver earnings
export const importRatesPreview = (csv_text) => api.post('/transport/freight-rates/import-preview', { csv_text });
export const importRatesCommit = (csv_text) => api.post('/transport/freight-rates/import-commit', { csv_text });
export const getDriverEarnings = (driverId, params = {}) => api.get(`/transport/drivers/${driverId}/earnings`, { params });
export const markDriverPaid = (jobId, p) => api.post(`/transport/jobs/${jobId}/mark-paid`, p);
export const markDriverUnpaid = (jobId) => api.post(`/transport/jobs/${jobId}/mark-unpaid`);

// iter220 — Vehicle suggest + Subbie remittance PDF
export const suggestVehicle = (jobId, planned_date) => api.get(`/transport/jobs/${jobId}/suggest-vehicle`, { params: planned_date ? { planned_date } : {} });
export const driverEarningsPdfUrl = (driverId, from_date, to_date) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const p = new URLSearchParams();
  if (from_date) p.set('from_date', from_date);
  if (to_date) p.set('to_date', to_date);
  const q = p.toString();
  return `${base}/api/transport/drivers/${driverId}/earnings/pdf${q ? `?${q}` : ''}`;
};

// iter221 — Multi-depot + Fuel tracking
export const listFuelReceipts = (vehicleId) => api.get(`/transport/vehicles/${vehicleId}/fuel`);
export const addFuelReceipt = (vehicleId, p) => api.post(`/transport/vehicles/${vehicleId}/fuel`, p);
export const deleteFuelReceipt = (vehicleId, receiptId) => api.delete(`/transport/vehicles/${vehicleId}/fuel/${receiptId}`);

// iter222 — Empty-leg marketplace
export const listEmptyLegs = (params = {}) => api.get('/transport/empty-legs', { params });
export const createEmptyLeg = (p) => api.post('/transport/empty-legs', p);
export const transitionEmptyLeg = (legId, p) => api.post(`/transport/empty-legs/${legId}/transition`, p);
export const deleteEmptyLeg = (legId) => api.delete(`/transport/empty-legs/${legId}`);
export const proposeEmptyLegForRun = (p) => api.post('/transport/routes/propose-empty-leg', p);

// iter223 — Exceptions + Analytics
export const listExceptionReasons = () => api.get('/transport/exception-reasons');
export const listExceptions = (params = {}) => api.get('/transport/exceptions', { params });
export const getTransportAnalytics = (params = {}) => api.get('/transport/analytics', { params });
export const podFileUrl = (path, token) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const p = encodeURIComponent(path);
  return `${base}/api/transport/pod/file?path=${p}${token ? `&token=${encodeURIComponent(token)}` : ''}`;
};
export const simulateOrder = (whId, pickListId) => api.get(`/warehouses/${whId}/layout/simulation/orders/${pickListId}`);
export const listScenarios = (whId) => api.get(`/warehouses/${whId}/scenarios`);
export const createScenario = (whId, body) => api.post(`/warehouses/${whId}/scenarios`, body);
export const getScenario = (whId, id) => api.get(`/warehouses/${whId}/scenarios/${id}`);
export const updateScenario = (whId, id, body) => api.put(`/warehouses/${whId}/scenarios/${id}`, body);
export const deleteScenario = (whId, id) => api.delete(`/warehouses/${whId}/scenarios/${id}`);
export const compareScenario = (whId, id, days = 30) => api.get(`/warehouses/${whId}/scenarios/${id}/compare`, { params: { days } });
export const exportScenarioPdf = (whId, id, days = 30) =>
  api.get(`/warehouses/${whId}/scenarios/${id}/export/pdf`, { params: { days }, responseType: 'blob' });
export const listCompletedPickListsForPlayback = (whId, limit = 50) => api.get('/pick-lists', { params: { warehouse_id: whId, status: 'completed', limit } });

// iter177 — AI Layout Optimiser (Phase 5)
export const runLayoutOptimizer = (whId, body) => api.post(`/warehouses/${whId}/layout/optimizer`, body);
export const applyOptimizerMoves = (whId, body) => api.post(`/warehouses/${whId}/layout/optimizer/apply`, body);


// Variants
export const getVariants = (params) => api.get('/variants', { params });
export const setVariantActive = (variantId, isActive) => api.patch(`/variants/${variantId}/active`, { is_active: !!isActive });
export const bulkSetVariantsActive = (variantIds, isActive) => api.post('/variants/bulk-active', { variant_ids: variantIds, is_active: !!isActive });
export const getVariant = (id) => api.get(`/variants/${id}`);
export const createVariant = (data) => api.post('/variants', data);
export const updateVariant = (id, data) => api.put(`/variants/${id}`, data);
export const backfillDefaultVariants = () => api.post('/skus/backfill-default-variants');
export const autoFillDimsFromCartons = () => api.post('/skus/auto-fill-dims-from-cartons');
export const bulkUpdateSkuPrice = (data) => api.post('/skus/bulk-update-price', data);
export const getSkuPriceHistory = (id) => api.get(`/skus/${id}/price-history`);
export const getVariantStockHistory = (id, days = 90) => api.get(`/variants/${id}/stock-history`, { params: { days } });
export const getStockoutRisk = (params = {}) => api.get('/skus/stockout-risk', { params });
export const getReorderSuggestions = (params = {}) => api.get('/skus/reorder-suggestions', { params });
export const draftPosFromReorderSuggestions = (data) => api.post('/skus/reorder-suggestions/draft-pos', data);
export const getRecentPriceChanges = (limit = 25) => api.get('/price-changes/recent', { params: { limit } });

// Locations
export const getLocations = (params) => api.get('/locations', { params });
export const getLocation = (id) => api.get(`/locations/${id}`);
export const createLocation = (data) => api.post('/locations', data);
export const updateLocation = (id, data) => api.put(`/locations/${id}`, data);
export const deleteLocation = (id, force = false) => api.delete(`/locations/${id}`, { params: { force } });

// Batches
export const getBatches = (params) => api.get('/batches', { params });
export const createBatch = (data) => api.post('/batches', data);
export const quarantineBatch = (id, reason) => api.post(`/batches/${id}/quarantine`, { reason });
export const releaseBatch = (id) => api.post(`/batches/${id}/release`);

// Inventory
export const getInventory = (params) => api.get('/inventory', { params });
export const getInventorySummary = () => api.get('/inventory/summary');
export const transferInventory = (data) => api.post('/inventory/transfer', data);
export const getInventoryAtLocation = (locationId) => api.get(`/inventory/at-location/${locationId}`);

// Orders
export const getOrders = (params) => api.get('/orders', { params });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const getOrderTimeline = (id) => api.get(`/orders/${id}/timeline`);
export const createOrder = (data) => api.post('/orders', data);
export const getOrderPickerInventory = (params) => api.get('/orders/picker/inventory', { params });
export const updateOrderStatus = (id, status) => api.put(`/orders/${id}/status`, { status });
export const importMockOrders = (channel, count) => api.post('/orders/import-mock', null, { params: { channel, count } });

// Pick Lists
export const getPickLists = (params) => api.get('/pick-lists', { params });
export const getPickList = (id) => api.get(`/pick-lists/${id}`);
export const createPickList = (data) => api.post('/pick-lists', data);
export const createBatchPickList = (data) => api.post('/pick-lists/batch', data);
export const assignPicker = (id, pickerId) => api.post(`/pick-lists/${id}/assign`, { picker_id: pickerId });
export const pickItem = (id, data) => api.post(`/pick-lists/${id}/pick-item`, data);
export const completePickList = (id) => api.post(`/pick-lists/${id}/complete`);

// Pack Tasks
export const getPackTasks = (params) => api.get('/pack-tasks', { params });
export const getPackTask = (id) => api.get(`/pack-tasks/${id}`);
export const createPackTask = (data) => api.post('/pack-tasks', data);
export const packItem = (id, data) => api.post(`/pack-tasks/${id}/pack-item`, data);
export const completePackTask = (id) => api.post(`/pack-tasks/${id}/complete`);

// Shipping
export const generateShippingLabel = (data) => api.post('/shipping/generate-label', data);
export const getShippingLabels = (params) => api.get('/shipping/labels', { params });
export const shipOrder = (orderId) => api.post('/shipping/ship-order', { order_id: orderId });
export const shipNow = (orderId, carrier) => api.post('/shipping/ship-now', { order_id: orderId, carrier });
export const bulkShipNow = (orderIds, carrier) => api.post('/shipping/bulk-ship-now', { order_ids: orderIds, carrier });
// Universal label-print endpoint — returns mode-specific payload (browser/zebra/printnode)
export const printShippingLabel = (trackingNumber, payload = {}) => api.post(`/shipping/labels/${trackingNumber}/print`, payload);
// Tenant printing settings
export const updatePrintingSettings = (data) => api.patch('/companies/me/settings/printing', data);
export const testPrintNodeKey = (api_key) => api.post('/printing/printnode/test', api_key ? { api_key } : {});
export const listPrintNodePrinters = () => api.get('/printing/printnode/printers');

// Stock Moves (location-to-location transfers)
export const stockMoveReasonCodes = () => api.get('/stock-moves/reason-codes');
export const listStockMoves = (params) => api.get('/stock-moves', { params });
export const getStockMove = (id) => api.get(`/stock-moves/${id}`);
export const createStockMove = (data) => api.post('/stock-moves', data);
export const executeStockMove = (id) => api.post(`/stock-moves/${id}/execute`);
export const cancelStockMove = (id) => api.post(`/stock-moves/${id}/cancel`);
export const quickStockMove = (data) => api.post('/stock-moves/quick-move', data);

// Lookup helpers used by scanner-driven flows
export const lookupLocationByCode = (code) => api.get(`/lookups/location/${encodeURIComponent(code)}`);
export const lookupItemByCode = (code) => api.get(`/lookups/item/${encodeURIComponent(code)}`);

// Best-fit location suggestion for put-away (used by Receiving flows)
export const getBestFitLocation = (variantId, quantity) =>
  api.get('/locations/best-fit', { params: { variant_id: variantId, quantity } });

// Receipts
export const getReceipts = (params) => api.get('/receipts', { params });
export const getReceipt = (id) => api.get(`/receipts/${id}`);
export const createReceipt = (data) => api.post('/receipts', data);
export const receiveItem = (id, data) => api.post(`/receipts/${id}/receive-item`, data);
export const completeReceipt = (id, body) => api.post(`/receipts/${id}/complete`, body || {});
export const getReceiptTemplates = (supplier) => api.get('/receipt-templates', { params: supplier ? { supplier } : {} });
export const saveReceiptTemplate = (data) => api.post('/receipt-templates', data);
export const deleteReceiptTemplate = (id) => api.delete(`/receipt-templates/${id}`);

// Cycle Counts
export const getCycleCounts = (params) => api.get('/cycle-counts', { params });
export const listCycleCounts = (params) => api.get('/cycle-counts', { params });
export const createCycleCount = (data) => api.post('/cycle-counts', data);
export const countItem = (id, data) => api.post(`/cycle-counts/${id}/count-item`, data);
export const cycleCountItem = (id, data) => api.post(`/cycle-counts/${id}/count-item`, data);
export const completeCycleCount = (id) => api.post(`/cycle-counts/${id}/complete`);
export const createCycleCountBySku = (sku_id) => api.post('/cycle-counts/by-sku', { sku_id });
export const getCycleCountLeaderboard = (lookback_days = 30, limit = 20) => api.get('/cycle-counts/leaderboard', { params: { lookback_days, limit } });
export const publishCycleCountLeaderboard = (lookback_days = 30) => api.post('/cycle-counts/leaderboard/publish', null, { params: { lookback_days } });

// ==== Inbound containers ====
export const getContainerDashboard = () => api.get('/containers/dashboard');
export const getContainers = (params) => api.get('/containers', { params });
export const getContainer = (id) => api.get(`/containers/${id}`);
export const createContainer = (data) => api.post('/containers', data);
export const importContainerAsn = (formData) => api.post('/containers/import-asn', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const parsePoPdf = (formData) => api.post('/containers/pdf-import/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const commitPoPdf = (data) => api.post('/containers/pdf-import/commit', data);
export const getPoPdfUsage = () => api.get('/containers/pdf-import/usage');

// ==== Add-on marketplace ====
export const getAddonCatalog = () => api.get('/billing/addons/catalog');
export const getActiveAddons = () => api.get('/billing/addons/active');
export const checkoutAddon = (key, originUrl) => api.post(`/billing/addons/${key}/checkout`, { addon_key: key, origin_url: originUrl });
export const addonCheckoutStatus = (sid) => api.get(`/billing/addons/checkout/status/${sid}`);
export const deactivateAddon = (key) => api.delete(`/billing/addons/${key}`);
export const getBillingUsage = () => api.get('/billing/usage');
export const arriveContainer = (id, data) => api.post(`/containers/${id}/arrive`, data);
export const receiveContainerLine = (id, data) => api.post(`/containers/${id}/receive`, data);
export const closeContainer = (id) => api.post(`/containers/${id}/close`);
export const getContainerVarianceReport = (id) => api.get(`/containers/${id}/variance-report`);
export const scanCarton = (id, data) => api.post(`/containers/${id}/cartons/scan`, data);
export const reportDamage = (id, data) => api.post(`/containers/${id}/damage`, data);
export const uploadContainerDocument = (id, data) => api.post(`/containers/${id}/documents`, data);
export const listContainerDocuments = (id) => api.get(`/containers/${id}/documents`);
export const getContainerDocument = (id, docId) => api.get(`/containers/${id}/documents/${docId}`);
export const deleteContainerDocument = (id, docId) => api.delete(`/containers/${id}/documents/${docId}`);
export const getPutawayPlan = (id) => api.get(`/containers/${id}/putaway-plan`);
export const executePutaway = (id, moves, config) => api.post(`/containers/${id}/putaway/execute`, { moves }, config);
export const cartonBarcodesPdfUrl = (id) => `${api.defaults.baseURL}/containers/${id}/cartons/barcodes.pdf`;

// Dispatch
export const getDispatchDashboard = () => api.get('/dispatch/dashboard');
export const getDispatches = (params) => api.get('/dispatch', { params });
export const getDispatch = (id) => api.get(`/dispatch/${id}`);
export const createDispatch = (data) => api.post('/dispatch', data);
export const updateDispatch = (id, data) => api.patch(`/dispatch/${id}`, data);
export const transitionDispatch = (id, newStatus, reason) => api.post(`/dispatch/${id}/status`, { new_status: newStatus, reason });
export const allocateDispatchOrder = (id, data) => api.post(`/dispatch/${id}/allocate`, data);
export const deallocateDispatchOrder = (id, orderId) => api.delete(`/dispatch/${id}/allocate/${orderId}`);
export const loadScanDispatch = (id, data) => api.post(`/dispatch/${id}/load/scan`, data);
export const sealDispatch = (id, data) => api.post(`/dispatch/${id}/seal`, data);
export const cancelDispatch = (id, reason) => api.post(`/dispatch/${id}/cancel`, { new_status: 'cancelled', reason });
export const holdDispatch = (id, reason) => api.post(`/dispatch/${id}/hold`, { new_status: 'on_hold', reason });
export const uploadDispatchDoc = (id, data) => api.post(`/dispatch/${id}/documents`, data);
export const listDispatchDocs = (id) => api.get(`/dispatch/${id}/documents`);
export const getDispatchDoc = (id, docId) => api.get(`/dispatch/${id}/documents/${docId}`);
export const deleteDispatchDoc = (id, docId) => api.delete(`/dispatch/${id}/documents/${docId}`);
export const getDispatchAudit = (id) => api.get(`/dispatch/${id}/audit`);
// Dispatch Phase 2a
export const addDispatchPhoto = (id, data) => api.post(`/dispatch/${id}/photos`, data);
export const listDispatchPhotos = (id, category) => api.get(`/dispatch/${id}/photos`, { params: category ? { category } : {} });
export const deleteDispatchPhoto = (id, photoId) => api.delete(`/dispatch/${id}/photos/${photoId}`);
export const getDispatchOptimise = (id, params) => api.get(`/dispatch/${id}/optimise`, { params });
export const listDocks = (params) => api.get('/dispatch/docks', { params });
export const listFreeDocks = (minutes = 30, warehouse_id) =>
  api.get('/dispatch/docks/free-now', { params: { minutes, warehouse_id } });
export const getDocksHeatmap = (params) => api.get('/dispatch/docks/heatmap', { params });
export const heatmapAutoResolve = (payload) => api.post('/dispatch/docks/heatmap/auto-resolve', payload);
export const getFleetKpis = () => api.get('/dispatch/fleet-kpis');
export const getGpsHeatmap = (days = 30) => api.get('/dispatch/gps-heatmap', { params: { days } });

// Carrier portal handoff (Phase 3c)
export const generateCarrierHandoff = (dispatchId, payload) => api.post(`/dispatch/${dispatchId}/handoff/generate`, payload);
export const listCarrierHandoffs = (dispatchId) => api.get(`/dispatch/${dispatchId}/handoff`);
export const revokeCarrierHandoff = (dispatchId, token) => api.delete(`/dispatch/${dispatchId}/handoff/${token}`);
export const bookDock = (id, data) => api.post(`/dispatch/${id}/dock-booking`, data);
export const releaseDock = (id) => api.delete(`/dispatch/${id}/dock-booking`);
export const bookManualShipping = (id, data) => api.post(`/dispatch/${id}/manual-shipping`, data);
export const removeManualShipping = (id) => api.delete(`/dispatch/${id}/manual-shipping`);
export const dispatchManifestPdfUrl = (id) => `${api.defaults.baseURL}/dispatch/${id}/manifest.pdf`;
// Dispatch Phase 2b
export const dispatchBolPdfUrl = (id) => `${api.defaults.baseURL}/dispatch/${id}/bol.pdf`;
export const getDispatchLoadSequence = (id) => api.get(`/dispatch/${id}/load-sequence`);
export const markDispatchDelivered = (id, data) => api.post(`/dispatch/${id}/delivered`, data || {});
export const markDispatchReturned = (id, data) => api.post(`/dispatch/${id}/returned`, data || {});
export const getDetentionReport = (lookback_days = 90) => api.get('/dispatch/detention-report', { params: { lookback_days } });
export const getDigestHistory = (limit = 10) => api.get('/analytics/digest-history', { params: { limit } });
// Order-level manual shipping
export const bookOrderManualShipping = (id, data) => api.post(`/orders/${id}/manual-shipping`, data);
export const removeOrderManualShipping = (id) => api.delete(`/orders/${id}/manual-shipping`);
export const getLabourKpis = (lookback_days = 30) => api.get('/containers/labour-kpis', { params: { lookback_days } });
export const getSupplierScorecard = (lookback_days = 90) => api.get('/containers/supplier-scorecard', { params: { lookback_days } });
export const publishSupplierScorecard = (lookback_days = 90) => api.post('/containers/supplier-scorecard/publish', null, { params: { lookback_days } });
export const getCycleCount = (id) => api.get(`/cycle-counts/${id}`);
export const approveCycleCount = (id, note) => api.post(`/cycle-counts/${id}/approve`, { note });
export const rejectCycleCount = (id, note) => api.post(`/cycle-counts/${id}/reject`, { note });

// Adjustments
export const getAdjustments = (params) => api.get('/adjustments', { params });
export const createAdjustment = (data) => api.post('/adjustments', data);
export const approveAdjustment = (id) => api.post(`/adjustments/${id}/approve`);

// Reports
export const getDashboard = () => api.get('/reports/dashboard');
export const getMovementReport = (params) => api.get('/reports/movements', { params });
export const getPickerKpis = () => api.get('/reports/picker-kpis');
export const getLeaderboard = (params) => api.get('/reports/leaderboard', { params });
export const getScanLeaderboard = (params) => api.get('/reports/scan-leaderboard', { params });

// Barcode
export const lookupBarcode = (barcode) => api.get(`/barcode/lookup/${barcode}`);

// BOM & Manufacturing
export const getBOMs = (params) => api.get('/boms', { params });
export const getBOM = (id) => api.get(`/boms/${id}`);
export const createBOM = (data) => api.post('/boms', data);
export const deleteBOM = (id) => api.delete(`/boms/${id}`);
export const getBOMCost = (id, cost_method = 'most_recent') => api.get(`/boms/${id}/cost`, { params: { cost_method } });
export const getBOMTree = (id, quantity = 1) => api.get(`/boms/${id}/tree`, { params: { quantity } });
export const getBOMWhereUsed = (params) => api.get('/boms/where-used', { params });
export const mrpExplode = (payload) => api.post('/mrp/explode', payload);
export const getProductionOrders = (params) => api.get('/production-orders', { params });
export const createProductionOrder = (data) => api.post('/production-orders', data);
export const advancePOStage = (id, data) => api.post(`/production-orders/${id}/advance-stage`, data || {});
export const scanConsumeComponent = (id, data) => api.post(`/production-orders/${id}/scan-consume`, data);
export const scanYieldUnit = (id, data) => api.post(`/production-orders/${id}/scan-yield`, data);

// Warehouses
export const getWarehouses = () => api.get('/warehouses');
export const createWarehouse = (data) => api.post('/warehouses', data);
export const updateWarehouse = (id, data) => api.put(`/warehouses/${id}`, data);
export const setDefaultWarehouse = (id) => api.post(`/warehouses/${id}/set-default`);

// Alerts & Emails
export const checkLowStock = () => api.post('/alerts/check-low-stock');
export const getLowStockAlerts = () => api.get('/alerts/low-stock');
export const dismissAlert = (id) => api.post(`/alerts/${id}/dismiss`);
export const getEmails = (params) => api.get('/emails', { params });
export const setSkuThreshold = (id, threshold) => api.put(`/skus/${id}/threshold`, { low_stock_threshold: threshold });

// Excel Export URLs (for download)
export const excelExportUrl = (type) => `${API}/api/export/${type}.xlsx`;

// Reservations
export const getReservationsSummary = () => api.get('/reservations/summary');
export const releaseOrderReservation = (orderId) => api.post(`/orders/${orderId}/release-reservation`);

// Transfers
export const getTransfers = (params) => api.get('/transfers', { params });
export const getTransfer = (id) => api.get(`/transfers/${id}`);
export const createTransfer = (data) => api.post('/transfers', data);
export const shipTransfer = (id, body) => api.post(`/transfers/${id}/ship`, body || {});
export const receiveTransfer = (id, data) => api.post(`/transfers/${id}/receive`, data);
export const cancelTransfer = (id) => api.post(`/transfers/${id}/cancel`);

// Forecast
export const forecastSku = (id, params) => api.get(`/forecast/sku/${id}`, { params });
export const getMasterPlan = (params) => api.get('/forecast/master-plan', { params });
export const getSkuHistory = (id, params) => api.get(`/forecast/sku/${id}/history`, { params });

// Iter51 — master planning module
export const getForecastProjection = (params) => api.get('/forecast/projection', { params });
export const upsertCellOverride = (data) => api.post('/forecast/cell-overrides', data);
export const bulkUpsertCellOverrides = (data) => api.post('/forecast/cell-overrides/bulk', data);
export const deleteCellOverride = (skuId, weekOffset, field) =>
  api.delete(`/forecast/cell-overrides/${skuId}/${weekOffset}/${field}`);
export const updateCompanyBranding = (palette) =>
  api.patch('/companies/me/branding', { palette });
export const getForecastExceptions = (params) => api.get('/forecast/exceptions', { params });
export const getForecastAccuracy = (params) => api.get('/forecast/accuracy', { params });
export const getSuggestedReorders = (params) => api.get('/forecast/suggested-reorders', { params });
export const getForecastShortages = () => api.get('/forecast/shortages');
export const getForecastOverstock = (params) => api.get('/forecast/overstock', { params });

// Iter52 — manual adjustments (forecast planner overrides)
export const listAdjustments = (params) => api.get('/forecast/adjustments', { params });
export const createForecastAdjustment = (payload) => api.post('/forecast/adjustments', payload);
export const approveForecastAdjustment = (id) => api.put(`/forecast/adjustments/${id}/approve`);
export const rejectForecastAdjustment = (id, reason) => api.put(`/forecast/adjustments/${id}/reject`, { reason });
export const deleteForecastAdjustment = (id) => api.delete(`/forecast/adjustments/${id}`);
export const getAdjustmentsPendingCount = () => api.get('/forecast/adjustments/pending-count');

// Iter52 — planning settings
export const getPlanningSettings = () => api.get('/forecast/planning-settings');
export const updatePlanningSetting = (sku_id, patch) => api.put(`/forecast/planning-settings/${sku_id}`, patch);
export const bulkUpdatePlanningSettings = (rows) => api.put('/forecast/planning-settings/bulk', { rows });

// Iter54 — What-If Scenario simulator
export const simulateWhatIf = (payload) => api.post('/forecast/whatif/simulate', payload);
export const saveWhatIfScenario = (payload) => api.post('/forecast/whatif/save', payload);
export const listWhatIfScenarios = () => api.get('/forecast/whatif');
export const deleteWhatIfScenario = (id) => api.delete(`/forecast/whatif/${id}`);

// Iter55 — Promote What-If recommendation to Draft PO
export const promoteToDraftPo = (payload) => api.post('/forecast/whatif/promote-to-draft-po', payload);
export const listPoDrafts = (params) => api.get('/forecast/whatif/drafts', { params });
export const deletePoDraft = (id) => api.delete(`/forecast/whatif/drafts/${id}`);

// Iter57 — Consolidate-to-Vendor
export const getConsolidatedDrafts = (params) => api.get('/po-drafts/consolidated', { params });
export const assignDraftSupplier = (id, supplier_name) => api.patch(`/po-drafts/${id}/supplier`, { supplier_name });
export const convertDraftsToContainer = (payload) => api.post('/po-drafts/convert-to-container', payload);

// Iter55 — Customer-dimension projection
export const getProjectionByCustomer = (sku_id, params) =>
  api.get('/forecast/projection-by-customer', { params: { sku_id, ...(params || {}) } });

// Iter55 — Scheduler audit log
export const getSchedulerAuditLog = (params) => api.get('/admin/scheduler-runs/audit', { params });

// Warehouse KPIs
export const getWarehouseKpis = (params) => api.get('/reports/warehouse-kpis', { params });

// Seed data
export const seedDemoData = (force = false) => api.post(`/seed/demo-data?force=${force}`);
export const resetDemoData = () => api.delete('/seed/demo-data');

// PDF shipping label URL (for inline view/download)
export const shippingLabelPdfUrl = (tracking_number) => `${API}/api/shipping/labels/${tracking_number}/pdf`;
// ZPL shipping label URL (for Zebra printers)
export const shippingLabelZplUrl = (tracking_number) => `${API}/api/shipping/labels/${tracking_number}/zpl`;

// Users
export const getUsers = (params) => api.get('/users', { params });
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Retention / Archival
export const getRetentionPolicies = () => api.get('/retention/policies');
export const updateRetentionPolicy = (entity, data) => api.put(`/retention/policies/${entity}`, data);
export const getRetentionStats = () => api.get('/retention/stats');
export const runRetention = () => api.post('/retention/run');
export const getRetentionRuns = (limit = 20) => api.get('/retention/runs', { params: { limit } });
export const getArchivedRecords = (entity, skip = 0, limit = 50) => api.get(`/retention/archive/${entity}`, { params: { skip, limit } });
export const restoreArchivedRecord = (entity, id) => api.post(`/retention/restore/${entity}/${id}`);

// Analytics
export const getVelocityClassification = (lookback_days = 90) => api.get('/analytics/velocity', { params: { lookback_days } });
export const getSkuAnalytics = (skuId, lookback_days = 90) => api.get(`/analytics/sku/${skuId}`, { params: { lookback_days } });
export const getTopWriteOffs = (lookback_days = 30, limit = 10) => api.get('/analytics/write-offs/top', { params: { lookback_days, limit } });
export const getWriteOffAlertConfig = () => api.get('/alerts/write-off-threshold');
export const putWriteOffAlertConfig = (payload) => api.put('/alerts/write-off-threshold', payload);
export const runWriteOffScan = () => api.post('/alerts/write-off-threshold/run');
export const getWriteOffAlertHistory = (limit = 50) => api.get('/alerts/write-off-threshold/history', { params: { limit } });

// Audit log
export const getAuditLogs = (params) => api.get('/audit-logs', { params });
export const getAuditSummary = (lookback_days = 7) => api.get('/audit-logs/summary', { params: { lookback_days } });

// Scheduler runs (per-tenant, admin only)
export const getSchedulerSummary = () => api.get('/admin/scheduler-runs/summary');
export const getSchedulerRuns = (params = {}) => api.get('/admin/scheduler-runs', { params });
export const triggerSchedulerJob = (job) => api.post(`/admin/scheduler-runs/${job}/trigger`);
export const dryRunSchedulerJob = (job) => api.post(`/admin/scheduler-runs/${job}/dry-run`);
export const getSchedulerConfig = () => api.get('/admin/scheduler-runs/config');
export const putSchedulerJobConfig = (job, patch) => api.put(`/admin/scheduler-runs/config/${job}`, patch);

// Compliance export
export const getComplianceEntities = () => api.get('/compliance/entities');
export const previewComplianceExport = (data) => api.post('/compliance/preview', data);
export const complianceExportUrl = `${API}/api/compliance/export`; // POST + download

// Custom reports
export const runCustomReport = (data) => api.post('/reports/custom', data);
export const getCustomReportFields = (entity) => api.get(`/reports/custom/fields/${entity}`);

// Bulk shipping labels ZIP
export const bulkShippingLabelsUrl = `${API}/api/shipping/bulk-labels`;

// Health Score
export const getHealthScore = () => api.get('/health-score');

// CSV Import
export const getImportSchemas = () => api.get('/import/schemas');
export const importSampleCsvUrl = (entity, prefill = false, { missingDimsOnly = false } = {}) => {
  const params = [];
  if (missingDimsOnly) params.push('missing_dims_only=true');
  else if (prefill) params.push('prefill=true');
  const qs = params.length ? `?${params.join('&')}` : '';
  return `${api.defaults.baseURL}/import/sample/${entity}.csv${qs}`;
};
export const previewImport = (entity, file) => {
  const fd = new FormData();
  fd.append('entity', entity);
  fd.append('file', file);
  return api.post('/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const commitImport = (entity, file, skipDuplicates = true) => {
  const fd = new FormData();
  fd.append('entity', entity);
  fd.append('file', file);
  fd.append('skip_duplicates', skipDuplicates);
  return api.post('/import/commit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const downloadImportErrors = (entity, file) => {
  const fd = new FormData();
  fd.append('entity', entity);
  fd.append('file', file);
  return api.post('/import/errors.csv', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob',
  });
};
export const getImportHistory = (limit = 20) => api.get('/import/history', { params: { limit } });

// 2FA TOTP
export const get2faStatus = () => api.get('/auth/2fa/status');
export const enable2faInit = () => api.post('/auth/2fa/enable-init');
export const enable2faConfirm = (code) => api.post('/auth/2fa/enable-confirm', { code });
export const disable2fa = (password, code) => api.post('/auth/2fa/disable', { password, code });
export const loginWith2fa = (data) => api.post('/auth/login-2fa', data);

// WebSocket helper
export const wsEventsUrl = () => {
  const httpUrl = API || '';
  const wsUrl = httpUrl.replace(/^http/, 'ws');
  return `${wsUrl}/api/ws/events`;
};

// Changelog / Release Notes
export const getChangelog = () => api.get('/changelog');
export const getChangelogAll = () => api.get('/changelog/all');
export const createRelease = (data) => api.post('/changelog', data);
export const updateRelease = (version, data) => api.put(`/changelog/${version}`, data);
export const deleteRelease = (version) => api.delete(`/changelog/${version}`);

// Location reports
export const getEmptyLocations = (params) => api.get('/reports/empty-locations', { params });
export const getCycleCountCoverage = (params) => api.get('/reports/cycle-count-coverage', { params });
export const createCountFromOverdue = (data) => api.post('/reports/cycle-count-coverage/create-count', data);
export const getCycleCountSchedule = () => api.get('/reports/cycle-count-schedule');
export const updateCycleCountSchedule = (data) => api.put('/reports/cycle-count-schedule', data);
export const runScheduleNow = () => api.post('/reports/cycle-count-schedule/run-now');
export const testScheduleNotification = () => api.post('/reports/cycle-count-schedule/test-notification');

// Broadcast announcements
export const sendBroadcast = (data) => api.post('/notifications/broadcast', data);
export const getBroadcasts = () => api.get('/notifications/broadcast');
export const cancelBroadcast = (id) => api.delete(`/notifications/broadcast/${id}`);

// Customer Returns / RMAs
export const listRMAs = (params) => api.get('/returns', { params });
export const getRMA = (id) => api.get(`/returns/${id}`);
export const createRMA = (data) => api.post('/returns', data);
export const approveRMA = (id, data) => api.post(`/returns/${id}/approve`, data);
export const rejectRMA = (id, data) => api.post(`/returns/${id}/reject`, data);
export const advanceRMAStatus = (id, data) => api.patch(`/returns/${id}/status`, data);
export const getRMASummary = () => api.get('/returns/stats/summary');

// Quarantine review (inventory-level)
export const listQuarantine = () => api.get('/quarantine-review');
export const quarantineBulkAction = (data) => api.post('/quarantine-review/bulk', data);
export const listWriteOffs = () => api.get('/quarantine-review/write-offs');

// Data Health
export const getDataHealthSummary = () => api.get('/data-health/summary');
export const getDataHealthCheck = (key) => api.get(`/data-health/${key}`);
export const cleanupDataHealthCheck = (key) => api.post(`/data-health/${key}/cleanup`);

// Container landed costs
export const previewLandedCosts = (containerId) =>
  api.get(`/containers/${containerId}/landed-costs/preview`);
export const finalizeLandedCosts = (containerId, body = {}) =>
  api.post(`/containers/${containerId}/landed-costs/finalize`, body);
export const resetLandedCosts = (containerId) =>
  api.post(`/containers/${containerId}/landed-costs/reset`);
export const patchContainerCosts = (containerId, body) =>
  api.patch(`/containers/${containerId}/costs`, body);

// Margin analytics
export const getMarginsSummary = () => api.get('/analytics/margins/summary');
export const getMargins = (params) => api.get('/analytics/margins', { params });
export const applyFloor = (skuId, body = {}) =>
  api.post(`/analytics/margins/${skuId}/apply-floor`, body);
export const applyFloorBulk = (body = {}) =>
  api.post('/analytics/margins/apply-floor/bulk', body);
export const undoFloor = (skuId) =>
  api.post(`/analytics/margins/${skuId}/undo-floor`);
export const updateMarginSettings = (body) =>
  api.patch('/companies/me/settings/margin', body);

// 3PL Billing
export const get3plPnl = () => api.get('/3pl/pnl');
export const get3plMerchantDrillDown = (merchantId, params = {}) =>
  api.get(`/3pl/merchants/${merchantId}/drill-down`, { params });
export const get3plSummary = () => api.get('/3pl/summary');
export const ensure3plDefaultMerchant = () => api.post('/3pl/merchants/default');
export const list3plMerchants = (params) => api.get('/3pl/merchants', { params });
export const create3plMerchant = (body) => api.post('/3pl/merchants', body);
export const update3plMerchant = (id, body) => api.patch(`/3pl/merchants/${id}`, body);
export const get3plRateCard = (merchantId) => api.get(`/3pl/merchants/${merchantId}/rate-card`);
export const upsert3plRateCard = (merchantId, body) =>
  api.put(`/3pl/merchants/${merchantId}/rate-card`, body);
export const list3plCharges = (params) => api.get('/3pl/charges', { params });
export const create3plManualCharge = (body) => api.post('/3pl/charges', body);
export const void3plCharge = (id) => api.post(`/3pl/charges/${id}/void`);
export const generate3plInvoices = (body) => api.post('/3pl/invoices/generate', body);
export const list3plInvoices = (params) => api.get('/3pl/invoices', { params });
export const get3plInvoice = (id) => api.get(`/3pl/invoices/${id}`);
export const markPaid3plInvoice = (id) => api.post(`/3pl/invoices/${id}/mark-paid`);
export const void3plInvoice = (id) => api.post(`/3pl/invoices/${id}/void`);
export const send3plInvoice = (id, body = {}) => api.post(`/3pl/invoices/${id}/send`, body);
export const send3plAllDrafts = (body = {}) => api.post('/3pl/invoices/send-all-drafts', body);
export const invoice3plPdfUrl = (id) =>
  `${API_BASE}/api/3pl/invoices/${id}/pdf`;

// Pallet Management
export const seedPalletDefaults = () => api.post('/pallets/seed-defaults');
export const listPalletTypes = () => api.get('/pallets/types');
export const createPalletType = (body) => api.post('/pallets/types', body);
export const updatePalletType = (id, body) => api.patch(`/pallets/types/${id}`, body);
export const deletePalletType = (id) => api.delete(`/pallets/types/${id}`);
export const listPalletMovements = (params) => api.get('/pallets/movements', { params });
export const createPalletMovement = (body) => api.post('/pallets/movements', body);
export const voidPalletMovement = (id) => api.post(`/pallets/movements/${id}/void`);
export const getPalletBalances = (params) => api.get('/pallets/balances', { params });
export const getPalletSummary = () => api.get('/pallets/summary');
export const getPalletVelocity = (params) => api.get('/pallets/velocity', { params });
export const sendPalletClaimEmail = (movementId, body) =>
  api.post(`/pallets/movements/${movementId}/claim/send`, body);
export const sendPalletDraftClaims = () =>
  api.post('/pallets/movements/send-draft-claims', {});
export const sendPalletRecallReminder = (body) =>
  api.post('/pallets/velocity/recall', body);
export const createPalletExchange = (body) => api.post('/pallets/exchanges', body);
export const listPalletReconciliations = (params) =>
  api.get('/pallets/reconciliations', { params });
export const createPalletReconciliation = (body) =>
  api.post('/pallets/reconciliations', body);
export const getPalletReconciliation = (id) =>
  api.get(`/pallets/reconciliations/${id}`);
export const signoffPalletReconciliation = (id) =>
  api.post(`/pallets/reconciliations/${id}/sign-off`);
export const palletReconciliationCsvUrl = (id, fmt = 'portfolio_plus') =>
  `${API_BASE}/api/pallets/reconciliations/${id}/export.csv?fmt=${fmt}`;

// Pallet Loss Investigator — trace where pooled pallets went missing
export const getPalletAuditLedger = (params) =>
  api.get('/pallets/audit/ledger', { params });
export const getPalletCounterpartyOutstanding = (params) =>
  api.get('/pallets/audit/counterparty-outstanding', { params });
export const getPalletUnreturnedOutbounds = (params) =>
  api.get('/pallets/audit/unreturned-outbounds', { params });
export const sendPalletRecallReminders = (params) =>
  api.post('/pallets/audit/send-recall-reminders', null, { params });
export const getPalletReconciliationPrefill = (params) =>
  api.get('/pallets/audit/prefill-reconciliation', { params });
export const getPalletAuditSparkline = (params) =>
  api.get('/pallets/audit/sparkline', { params });
export const getPalletAuditSettings = () =>
  api.get('/pallets/audit/settings');
export const updatePalletAuditSettings = (body) =>
  api.put('/pallets/audit/settings', body);
export const getPalletMagicLinkStats = (params) =>
  api.get('/pallets/audit/magic-link-stats', { params });
export const getMerchantTrustScores = (params) =>
  api.get('/pallets/audit/merchant-trust', { params });
export const getPalletRoiKpis = () => api.get('/pallets/audit/roi-kpis');
export const getPalletRoiSparkline = (months = 6) =>
  api.get('/pallets/audit/roi-sparkline', { params: { months } });
export const getPalletLossesAvoidedYtd = () =>
  api.get('/pallets/audit/losses-avoided-ytd');
export const palletAuditCsvUrl = (params) => {
  const q = new URLSearchParams(params).toString();
  return `${API_BASE}/api/pallets/audit/export.csv?${q}`;
};

// Pallet Pooler integration (CHEP / Loscam)
export const getPoolerSettings = () => api.get('/pallets/pooler/settings');
export const updatePoolerSettings = (body) => api.put('/pallets/pooler/settings', body);
export const setMerchantPoolerAccounts = (merchantId, body) =>
  api.put(`/pallets/pooler/merchants/${merchantId}/accounts`, body);
export const refreshPoolerBalance = (body) =>
  api.post('/pallets/pooler/balance/refresh', body);
export const getPoolerReconciliation = () =>
  api.get('/pallets/pooler/balance/reconciliation');
export const fetchPoolerStatement = (body) =>
  api.post('/pallets/pooler/statement/fetch', body);
export const getPoolerSyncLog = (params) =>
  api.get('/pallets/pooler/sync-log', { params });
export const getMovementSyncStatus = (params) =>
  api.get('/pallets/pooler/movements/sync-status', { params });
export const syncMovementNow = (movementId) =>
  api.post(`/pallets/pooler/movements/${movementId}/sync`);
export const runPoolerSyncNow = () =>
  api.post('/pallets/pooler/sync/run-now');

// Tax invoices (created on pack-complete, finalised on ship)
export const listInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const getInvoiceByOrder = (orderId) => api.get(`/invoices/by-order/${orderId}`);
export const generateInvoice = (orderId) => api.post('/invoices/generate', { order_id: orderId });
export const finaliseInvoice = (orderId) => api.post('/invoices/finalise', { order_id: orderId });
export const regenerateInvoice = (orderId) => api.post('/invoices/regenerate', { order_id: orderId });
export const emailInvoice = (id) => api.post(`/invoices/${id}/email`);
export const getInvoiceSettings = () => api.get('/invoices/settings/current');
export const updateInvoiceSettings = (body) => api.put('/invoices/settings/current', body);

// iter225 — Company-level tenant settings (timezone)
export const getCompanySettings = () => api.get('/company/settings');
export const updateCompanySettings = (body) => api.put('/company/settings', body);

// iter226 — Competition Demo Mode
export const bootstrapCompetitionDemo = () => api.post('/competition/demo/bootstrap');
export const resetCompetitionDemo = () => api.post('/competition/demo/reset');
export const getCompetitionState = () => api.get('/competition/demo/state');
export const completeCompetitionObjective = (objective_key) =>
  api.post('/competition/demo/challenge/complete-objective', { objective_key });
export const trackCompetitionEvent = (event, scenario, detail) =>
  api.post('/competition/demo/track', { event, scenario, detail });
export const runCompetitionWhatIf = (body) => api.post('/competition/demo/whatif', body);
export const getCompetitionAbcVelocities = () => api.get('/competition/demo/abc-velocities');
export const getCompetitionPalletStatement = (pool_code = 'CHEP') =>
  api.get('/competition/demo/pallet-statement', { params: { pool_code } });
export const reconcileCompetitionPallet = (pool_code) =>
  api.post('/competition/demo/pallet-reconcile', { pool_code });
export const getCompetitionAnalytics = () => api.get('/competition/demo/analytics');
export const rotateCompetitionDemoPassword = (password) =>
  api.post('/competition/demo/rotate-password', password ? { password } : {});
export const listJudgeCodes = () => api.get('/competition/demo/judge-codes');
export const createJudgeCode = (body) =>
  api.post('/competition/demo/judge-codes', body);
export const revokeJudgeCode = (codeId) =>
  api.post(`/competition/demo/judge-codes/${codeId}/revoke`);
export const redeemJudgeCode = (code) =>
  api.post('/competition/demo/redeem-code', { code });
export const syncCompetitionOfflineActions = (actions) =>
  api.post('/competition/demo/offline-sync', { actions });

export const invoicePdfUrl = (id) => `${API_BASE}/api/invoices/${id}/pdf`;

// Outstanding-invoices Kanban
export const getOutstandingKanban = () => api.get('/invoices/outstanding/kanban');
export const markInvoicePaid = (id, body) => api.post(`/invoices/${id}/mark-paid`, body || {});
export const markInvoiceUnpaid = (id) => api.post(`/invoices/${id}/mark-unpaid`);
export const sendInvoiceReminder = (id, body) =>
  api.post(`/invoices/${id}/send-reminder`, body || { origin_url: window.location.origin });
export const getInvoiceReminders = (id) => api.get(`/invoices/${id}/reminders`);
export const setInvoiceSkipAutoReminders = (id, skip, propagateToOrder = true) =>
  api.post(`/invoices/${id}/skip-auto-reminders`, { skip, propagate_to_order: propagateToOrder });
export const getCustomerCard = (email) =>
  api.get(`/invoices/customers/by-email/${encodeURIComponent(email)}`);
export const bulkSkipCustomer = (email, skip) =>
  api.post(`/invoices/customers/by-email/${encodeURIComponent(email)}/skip-auto-reminders`, { skip });
export const sendCustomerStatement = (email, sendEmail = true) =>
  api.post(`/invoices/customers/by-email/${encodeURIComponent(email)}/statement`,
    { send_email: sendEmail, origin_url: window.location.origin });
export const customerStatementPdfUrl = (email) =>
  `${API_BASE}/api/invoices/customers/by-email/${encodeURIComponent(email)}/statement.pdf`;
export const getCustomersList = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.has_overdue) qs.set('has_overdue', 'true');
  if (params.auto_remind) qs.set('auto_remind', params.auto_remind);
  if (params.sort) qs.set('sort', params.sort);
  return api.get(`/invoices/customers/list?${qs.toString()}`);
};
export const sendBulkStatements = (emails) =>
  api.post(`/invoices/customers/bulk-statement`, { emails, origin_url: window.location.origin });

// AR follow-up tasks
export const createArFollowup = (body) => api.post(`/ar/followups`, body);
export const listArFollowups = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.set(k, v));
  return api.get(`/ar/followups?${qs.toString()}`);
};
export const updateArFollowup = (id, body) => api.patch(`/ar/followups/${id}`, body);
export const deleteArFollowup = (id) => api.delete(`/ar/followups/${id}`);
export const myArFollowupsWidget = () => api.get(`/ar/followups/me/widget`);
export const listTenantUsers = () => api.get(`/auth/users`);

// Pricing module
export const listPricingProfiles = () => api.get(`/pricing/profiles`);
export const createPricingProfile = (body) => api.post(`/pricing/profiles`, body);
export const updatePricingProfile = (id, body, reason) =>
  api.patch(`/pricing/profiles/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, body);
export const deletePricingProfile = (id) => api.delete(`/pricing/profiles/${id}`);
export const listPriceRules = () => api.get(`/pricing/rules`);
export const createPriceRule = (body) => api.post(`/pricing/rules`, body);
export const updatePriceRule = (id, body, reason) =>
  api.patch(`/pricing/rules/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, body);
export const deletePriceRule = (id) => api.delete(`/pricing/rules/${id}`);
export const listRateCards = () => api.get(`/pricing/rate-cards`);
export const createRateCard = (body) => api.post(`/pricing/rate-cards`, body);
export const updateRateCard = (id, body) => api.patch(`/pricing/rate-cards/${id}`, body);
export const deleteRateCard = (id) => api.delete(`/pricing/rate-cards/${id}`);
export const listCustomerSkus = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && qs.set(k, v));
  return api.get(`/pricing/customer-skus?${qs.toString()}`);
};
export const upsertCustomerSku = (body) => api.post(`/pricing/customer-skus`, body);
export const deleteCustomerSku = (id) => api.delete(`/pricing/customer-skus/${id}`);
export const resolvePrice = (body) => api.post(`/pricing/resolve`, body);
export const listPriceAudit = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && qs.set(k, v));
  return api.get(`/pricing/audit?${qs.toString()}`);
};

// Pricing — Profitability dashboard
export const getProfitability = (windowDays = 30, groupBy = 'customer') =>
  api.get(`/pricing/profitability?window_days=${windowDays}&group_by=${groupBy}`);

// Pricing — Quote / Contract workflow
export const listQuotes = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && qs.set(k, v));
  return api.get(`/pricing/quotes?${qs.toString()}`);
};
export const getQuote = (id) => api.get(`/pricing/quotes/${id}`);
export const createQuote = (body) => api.post(`/pricing/quotes`, body);
export const updateQuote = (id, body) => api.patch(`/pricing/quotes/${id}`, body);
export const submitQuote = (id) => api.post(`/pricing/quotes/${id}/submit`);
export const approveQuote = (id, reason) =>
  api.post(`/pricing/quotes/${id}/approve${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
export const rejectQuote = (id, reason) =>
  api.post(`/pricing/quotes/${id}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
export const sendQuote = (id) => api.post(`/pricing/quotes/${id}/send`);
export const replayQuoteWebhook = (id) => api.post(`/pricing/quotes/${id}/replay-webhook`);
export const deleteQuote = (id) => api.delete(`/pricing/quotes/${id}`);
export const publicQuoteUrl = (token) => `/quote/${token}`;
export const fetchPublicQuote = (token) => api.get(`/pricing/public/quotes/${token}`);
// Promo / discount codes (signup discounts)
export const validatePromoCode = (code) => api.post(`/promo-codes/validate`, { code });
export const listPromoCodes = () => api.get(`/admin/promo-codes`);
export const createPromoCode = (body) => api.post(`/admin/promo-codes`, body);
export const updatePromoCode = (id, body) => api.patch(`/admin/promo-codes/${id}`, body);
export const deletePromoCode = (id) => api.delete(`/admin/promo-codes/${id}`);
export const listPromoRedemptions = (id) => api.get(`/admin/promo-codes/${id}/redemptions`);

// Per-tenant order discount codes (iter154 — different from promo/SaaS codes above)
export const listOrderDiscounts = () => api.get(`/order-discounts`);
export const createOrderDiscount = (body) => api.post(`/order-discounts`, body);
export const updateOrderDiscount = (id, body) => api.patch(`/order-discounts/${id}`, body);
export const deleteOrderDiscount = (id) => api.delete(`/order-discounts/${id}`);
export const listOrderDiscountRedemptions = (id) => api.get(`/order-discounts/${id}/redemptions`);
export const validateOrderDiscount = (body) => api.post(`/order-discounts/validate`, body);


// Picking — sort strategy + ABC velocity
export const getPickingSettings = () => api.get(`/picking/settings`);
export const updatePickingSettings = (body) => api.put(`/picking/settings`, body);
export const recomputeAbc = (windowDays = 90) => api.post(`/picking/abc/recompute?window_days=${windowDays}`);

export const respondPublicQuote = (token, accept, note) =>
  api.post(`/pricing/public/quotes/${token}/respond?accept=${accept ? 'true' : 'false'}${note ? `&customer_note=${encodeURIComponent(note)}` : ''}`);

// Pricing — quote webhook settings + log
export const getPricingSettings = () => api.get(`/pricing/settings`);
export const updatePricingSettings = (body) => api.put(`/pricing/settings`, body);
export const listQuoteWebhookLog = (quoteId, limit = 50) => {
  const qs = new URLSearchParams();
  if (quoteId) qs.set('quote_id', quoteId);
  if (limit) qs.set('limit', limit);
  return api.get(`/pricing/webhook-log?${qs.toString()}`);
};
export const getArEffectiveness = (windowDays = 30) =>
  api.get(`/invoices/analytics/ar-effectiveness?window_days=${windowDays}`);
export const arEffectivenessCsvUrl = (windowDays = 30) =>
  `${API_BASE}/api/invoices/analytics/ar-effectiveness.csv?window_days=${windowDays}`;
export const previewVarianceDigest = (body) =>
  api.post('/pallets/pooler/variance-digest/preview', body || { refresh_first: true });
export const sendVarianceDigestNow = () =>
  api.post('/pallets/pooler/variance-digest/send-now');
export const getVarianceDigestHistory = (params) =>
  api.get('/pallets/pooler/variance-digest/history', { params });
export const palletClaimPdfUrl = (movementId) =>
  `${API_BASE}/api/pallets/movements/${movementId}/claim.pdf`;
export const addPalletMovementPhoto = (id, body) =>
  api.post(`/pallets/movements/${id}/photos`, body);
export const listPalletMovementPhotos = (id) =>
  api.get(`/pallets/movements/${id}/photos`);
export const deletePalletMovementPhoto = (id, photoId) =>
  api.delete(`/pallets/movements/${id}/photos/${photoId}`);

export default api;
